/* ============================================
   MessageInput — Chat input bar
   Text + emoji picker + quick photo + attachment menu
   + voice recording + schedule message support
   ============================================ */
import { useState, useRef, useCallback, useEffect } from 'react'
import { IconPaperclip, IconMicrophone, IconSend, IconImage, IconFileText, IconPlus, IconClock, IconX } from '@/components/icons/Icons'
import type { Mensaje } from '@/lib/types'
import FilePreview from './FilePreview'
import AudioRecorder from './AudioRecorder'
import ScheduleDatePicker from './ScheduleDatePicker'
import EmojiPicker from './EmojiPicker'
import { showToast } from '@/components/layout/Toast'

export interface ScheduleMessageData {
  tipo: 'texto' | 'imagen' | 'video' | 'documento'
  contenido?: string
  file?: File
  scheduled_at: string
}

interface MessageInputProps {
  onSendText: (text: string) => void
  onSendMedia: (file: File) => void
  onSendAudio: (blob: Blob, duration: number) => void
  onScheduleMessage: (data: ScheduleMessageData) => void
  onFileSelected: (file: File) => void
  sending: boolean
  replyingTo: Mensaje | null
  onCancelReply: () => void
}

const MAX_FILE_SIZE = 16 * 1024 * 1024 // 16 MB
const IMAGE_ACCEPT = 'image/jpeg,image/png,image/gif,image/webp,video/mp4,video/webm'
const DOC_ACCEPT = '.pdf,.doc,.docx,.xls,.xlsx'

function inferTipoFromFile(file: File): 'imagen' | 'video' | 'documento' {
  if (file.type.startsWith('image/')) return 'imagen'
  if (file.type.startsWith('video/')) return 'video'
  return 'documento'
}

function mediaLabel(tipo: string): string {
  switch (tipo) {
    case 'imagen': return 'Imagen'
    case 'audio': return 'Audio'
    case 'video': return 'Video'
    case 'documento': return 'Documento'
    default: return tipo
  }
}

export default function MessageInput({ onSendText, onSendMedia, onSendAudio, onScheduleMessage, onFileSelected, sending, replyingTo, onCancelReply }: MessageInputProps) {
  const [text, setText] = useState('')
  const [file, setFile] = useState<File | null>(null)
  const [recording, setRecording] = useState(false)
  const [showAttachMenu, setShowAttachMenu] = useState(false)
  const [showScheduler, setShowScheduler] = useState(false)
  const [showEmojiPicker, setShowEmojiPicker] = useState(false)
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const imageInputRef = useRef<HTMLInputElement>(null)
  const docInputRef = useRef<HTMLInputElement>(null)
  const menuRef = useRef<HTMLDivElement>(null)

  const adjustHeight = useCallback(() => {
    const ta = textareaRef.current
    if (!ta) return
    ta.style.height = 'auto'
    ta.style.height = `${Math.min(ta.scrollHeight, 120)}px`
  }, [])

  useEffect(() => {
    if (!showAttachMenu) return
    const handleClick = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setShowAttachMenu(false)
      }
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [showAttachMenu])

  const handleTextChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setText(e.target.value)
    adjustHeight()
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSend()
    }
  }

  const handleInsertEmoji = (emoji: string) => {
    const ta = textareaRef.current
    if (ta) {
      const start = ta.selectionStart
      const end = ta.selectionEnd
      const before = text.slice(0, start)
      const after = text.slice(end)
      const newText = before + emoji + after
      setText(newText)
      // Set cursor after inserted emoji
      requestAnimationFrame(() => {
        ta.focus()
        ta.selectionStart = ta.selectionEnd = start + emoji.length
      })
    } else {
      setText(prev => prev + emoji)
    }
    adjustHeight()
  }

  const handleSend = () => {
    if (sending) return

    if (file) {
      onSendMedia(file)
      setFile(null)
      return
    }

    const trimmed = text.trim()
    if (!trimmed) return

    onSendText(trimmed)
    setText('')
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto'
    }
  }

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = e.target.files?.[0]
    if (!selected) return

    if (selected.size > MAX_FILE_SIZE) {
      showToast('El archivo excede el limite de 16 MB', 'error')
      return
    }

    // Open preview modal via parent (ChatWindow)
    onFileSelected(selected)
    setShowAttachMenu(false)
    e.target.value = ''
  }

  const handleRecordSend = (blob: Blob, duration: number) => {
    setRecording(false)
    onSendAudio(blob, duration)
  }

  const handleRecordCancel = () => {
    setRecording(false)
  }

  const handleScheduleConfirm = (scheduledAt: Date) => {
    const data: ScheduleMessageData = {
      tipo: file ? inferTipoFromFile(file) : 'texto',
      scheduled_at: scheduledAt.toISOString(),
    }

    if (file) data.file = file

    const trimmed = text.trim()
    if (trimmed) data.contenido = trimmed

    if (!data.file && !data.contenido) {
      showToast('Escribe un mensaje o adjunta un archivo', 'error')
      return
    }

    onScheduleMessage(data)
    setFile(null)
    setText('')
    setShowScheduler(false)
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto'
    }
  }

  if (recording) {
    return (
      <div className="chat-input">
        <AudioRecorder onSend={handleRecordSend} onCancel={handleRecordCancel} />
      </div>
    )
  }

  const hasText = text.trim().length > 0
  const hasContent = hasText || !!file

  return (
    <div className="chat-input">
      {/* Reply banner */}
      {replyingTo && (
        <div className="chat-input__reply-banner">
          <div className="chat-input__reply-info">
            <span className="chat-input__reply-label">
              Respondiendo a {replyingTo.direccion === 'entrante' ? 'cliente' : 'tu mensaje'}
            </span>
            <span className="chat-input__reply-preview">
              {replyingTo.tipo !== 'texto'
                ? mediaLabel(replyingTo.tipo)
                : (replyingTo.contenido?.slice(0, 60) || '...')}
            </span>
          </div>
          <button
            className="chat-input__reply-cancel"
            onClick={onCancelReply}
            type="button"
            title="Cancelar respuesta"
          >
            <IconX size={16} />
          </button>
        </div>
      )}

      {/* File preview bar */}
      {file && (
        <FilePreview file={file} onRemove={() => setFile(null)} />
      )}

      <div className="chat-input__bar">
        {/* Quick photo button — direct camera/gallery access */}
        <button
          className="chat-input__btn chat-input__btn--camera"
          onClick={() => imageInputRef.current?.click()}
          type="button"
          title="Foto o video"
          disabled={sending}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
            <circle cx="12" cy="13" r="4" />
          </svg>
        </button>

        {/* Attach menu */}
        <div className="chat-attach" ref={menuRef}>
          <button
            className="chat-input__btn"
            onClick={() => setShowAttachMenu(v => !v)}
            type="button"
            title="Adjuntar"
            disabled={sending}
          >
            {showAttachMenu ? <IconPlus size={20} style={{ transform: 'rotate(45deg)', transition: 'transform 0.2s' }} /> : <IconPaperclip size={20} />}
          </button>

          {showAttachMenu && (
            <div className="chat-attach__menu">
              <button
                className="chat-attach__option"
                onClick={() => imageInputRef.current?.click()}
                type="button"
              >
                <span className="chat-attach__icon chat-attach__icon--photo">
                  <IconImage size={20} />
                </span>
                <span>Fotos y videos</span>
              </button>
              <button
                className="chat-attach__option"
                onClick={() => docInputRef.current?.click()}
                type="button"
              >
                <span className="chat-attach__icon chat-attach__icon--doc">
                  <IconFileText size={20} />
                </span>
                <span>Documento</span>
              </button>
            </div>
          )}

          <input
            ref={imageInputRef}
            type="file"
            accept={IMAGE_ACCEPT}
            onChange={handleFileSelect}
            style={{ display: 'none' }}
          />
          <input
            ref={docInputRef}
            type="file"
            accept={DOC_ACCEPT}
            onChange={handleFileSelect}
            style={{ display: 'none' }}
          />
        </div>

        {/* Text input */}
        <textarea
          ref={textareaRef}
          className="chat-input__text"
          placeholder="Escribe un mensaje..."
          value={text}
          onChange={handleTextChange}
          onKeyDown={handleKeyDown}
          rows={1}
          disabled={sending}
        />

        {/* Emoji picker button */}
        <div className="chat-emoji-wrap">
          <button
            className="chat-input__btn chat-input__btn--emoji"
            onClick={() => setShowEmojiPicker(v => !v)}
            type="button"
            title="Emojis"
            disabled={sending}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10" />
              <path d="M8 14s1.5 2 4 2 4-2 4-2" />
              <line x1="9" y1="9" x2="9.01" y2="9" />
              <line x1="15" y1="9" x2="15.01" y2="9" />
            </svg>
          </button>

          {showEmojiPicker && (
            <EmojiPicker
              onSelect={emoji => {
                handleInsertEmoji(emoji)
              }}
              onClose={() => setShowEmojiPicker(false)}
            />
          )}
        </div>

        {/* Schedule + Send / Mic */}
        {hasContent ? (
          <>
            <div className="chat-input__schedule-wrap">
              <button
                className="chat-input__btn chat-input__btn--schedule"
                onClick={() => setShowScheduler(v => !v)}
                type="button"
                title="Programar mensaje"
                disabled={sending}
              >
                <IconClock size={20} />
              </button>
              {showScheduler && (
                <ScheduleDatePicker
                  onSchedule={handleScheduleConfirm}
                  onClose={() => setShowScheduler(false)}
                />
              )}
            </div>

            <button
              className="chat-input__btn chat-input__btn--send"
              onClick={handleSend}
              type="button"
              title="Enviar"
              disabled={sending}
            >
              <IconSend size={20} />
            </button>
          </>
        ) : (
          <button
            className="chat-input__btn chat-input__btn--mic"
            onClick={() => setRecording(true)}
            type="button"
            title="Grabar audio"
            disabled={sending}
          >
            <IconMicrophone size={20} />
          </button>
        )}
      </div>
    </div>
  )
}
